import type { z } from 'zod';
import { match } from 'ts-pattern';

import { assertBalancedLinesSchemas } from '@fragment/accounting-validation';
import {
  getInstanceValueByAccountPath,
  getStructuralPath,
  getStructuralSubpaths,
  getSubpaths,
} from '@fragment/path-utils/path';
import type { SchemaLedgerAccountStatus } from '@fragment/test-utils/customerApi/customerApi';
import {
  MAX_CONSISTENT_BALANCE_UPDATES,
  MAX_TAG_COUNT,
  MAX_TAG_KEY_LENGTH,
  MAX_TAG_VALUE_LENGTH,
} from '@fragment/types/constants';
import { MAX_ENTRY_LINES } from '@fragment/types/entry';
import type {
  BaseLedgerEntries,
  SchemaLedgerEntityStatus,
} from '@fragment/types/schemas';
import type { SafeString } from '@fragment/types/strings';
import {
  MAX_FREEFORM_TEXT_LENGTH,
  ParameterizedAmount,
  ParameterizedString,
  safe,
  SafeStringSchema,
} from '@fragment/types/strings';
import { safeGetZodErrors } from '@fragment/zod-utils/src';

import type { transformAndValidateCoA } from './coa';
import { getSchemaObjectParameters } from '../../parameterization';
import {
  parameterizeSchemaLedgerEntryConditionInput,
  parameterizeSchemaLedgerLineInput,
  reparameterizeSchemaLedgerAccountInput,
  SchemaCurrencyMatchInput,
} from '../../types';
import {
  AccountCategory,
  AccountCategoryMutualExclusionError,
  getSchemaAccountCategory,
} from '../accountCategory';
import { isTotalBalanceSchema } from '../consistencyValidation';
import {
  getAccountStatus,
  getEntryStatus,
  getEntryTypeVersion,
} from '../status';
import { getStructuralPathToAccount } from './utilities';

type ValidationResult = {
  code?: string;
  message: string;
  path: (string | number)[];
};

type ValidateEntriesParams = {
  ledgerEntries: z.infer<typeof BaseLedgerEntries>;
  validatedCoA: ReturnType<typeof transformAndValidateCoA>['filledIn'];
};

type ValidateNonEmptySafeStringParams = {
  value: string;
  path: (string | number)[];
  name: string;
  entityName: string;
  maxLength?: number;
};

export type ValidatedAccount = ReturnType<
  typeof transformAndValidateCoA
>['filledIn']['accounts'][number];

export const validateNonEmptySafeString = ({
  value,
  path,
  name,
  entityName: structName,
  maxLength = MAX_FREEFORM_TEXT_LENGTH,
}: ValidateNonEmptySafeStringParams): ValidationResult[] =>
  (value === ''
    ? [
        {
          message: `${structName} must specify ${name}`,
          path,
        },
      ]
    : ([] as ValidationResult[])
  )
    .concat(
      SafeStringSchema.safeParse(value).success
        ? []
        : [
            {
              message: `${structName} ${name} ${value} cannot contain '#', '/' or ':'`,
              path,
            },
          ]
    )
    .concat(
      value.length > maxLength
        ? [
            {
              message: `${structName} ${name} must be <= ${maxLength} characters`,
              path,
            },
          ]
        : ([] as ValidationResult[])
    );

type FillInParams = Pick<ValidateEntriesParams, 'ledgerEntries'> & {
  accountPathToAccount: Map<string, ValidatedAccount>;
};

export const fillIn = ({
  ledgerEntries,
  accountPathToAccount,
}: FillInParams) => ({
  ...ledgerEntries,
  types: ledgerEntries.types.map((type) => ({
    ...type,
    type: type.type ?? '', // empty string is invalid but can be rendered
    lines: (type.lines ?? []).map((line) => {
      const path = line.account?.path
        ? getStructuralPath(line.account.path)
        : undefined;

      return {
        ...line,
        account: line.account ?? {
          path: '',
        },
        key: line.key ?? safe`''`,
        amount: line.amount ?? '',
        currency:
          line.currency ??
          (path ? accountPathToAccount.get(path)?.currency : undefined),
      };
    }),
    conditions: (type.conditions ?? []).map((condition) => {
      const path = condition.account?.path
        ? getStructuralPath(condition.account.path)
        : undefined;
      return {
        ...condition,
        account: condition.account ?? {
          path: '',
        },
        currency:
          condition.currency ??
          (path ? accountPathToAccount.get(path)?.currency : undefined),
      };
    }),
    tags: (type.tags ?? []).map((tag) => ({ ...tag })),
    groups: (type.groups ?? []).map((group) => ({ ...group })),
  })),
});

type ValidateUniqueXParams<T extends Record<string, any>> = {
  key: keyof T & string;
  items: T[];
  pathPrefix: (string | number)[];
  getErrorMessage: (item: T) => string;
};

export const groupByMap = <T, K>(
  array: T[],
  iteratee: (item: T) => K
): Map<K, T[]> => {
  return array.reduce((map, item) => {
    const key = iteratee(item);
    return map.set(key, [...(map.get(key) || []), item]);
  }, new Map());
};

/**
 * Given a list of items, returns a list of validation errors if any of the items have duplicate values for the given key.
 * @param key The key to check for uniqueness
 * @param items The list of items to check
 * @param pathPrefix The prefix of the path used to construct the object path in case of an error
 * @param getErrorMessage Error message formatter
 */
export const validateUniqueByKey = <T extends Record<string, any>>({
  key,
  items,
  pathPrefix,
  getErrorMessage,
}: ValidateUniqueXParams<T>): ValidationResult[] => {
  const types: [string, number, T][] = items.map((type, idx) => [
    type[key],
    idx,
    type,
  ]);
  return Array.from(groupByMap(types, ([type]) => type).entries())
    .filter(([_k, v]) => v.length > 1)
    .flatMap(([_k, vs]) =>
      vs.map(([_, v, item]) => ({
        message: getErrorMessage(item),
        // message: `${entityName} cannot have duplicate ${key} '${k}'`,
        path: [...pathPrefix, v, key],
      }))
    );
};

export const validateLineKey = (key: string): ValidationResult[] => {
  return validateNonEmptySafeString({
    value: key,
    path: ['key'],
    name: 'key',
    entityName: 'Ledger Line',
  });
};

export const validateLineAmount = (
  amount: string | undefined,
  entryType: string,
  accountPath: string,
  lineKey: string,
  entryHasTwoLines: boolean,
  entryHasAnyLineWithTx: boolean
): ValidationResult[] => {
  const validationResults: ValidationResult[] = safeGetZodErrors(
    ParameterizedAmount(),
    amount,
    ['amount']
  );
  if (!amount && (!entryHasTwoLines || !entryHasAnyLineWithTx)) {
    validationResults.push({
      message: `Must specify amount for line. Amount may only be left blank for 2-line entries reconciling to a linked account, since Fragment can infer it from the tx amount. (Entry: ${entryType}'s |  Line account path: ${accountPath} | Line key: ${lineKey})`,
      path: ['amount'],
    });
    // skipBalanceCheck = true; // If we're missing amounts then balance check is redundant
  }
  return validationResults;
};

export const getFullyParameterizedAccount = (
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  entryParameters: Record<string, string>
) => {
  const structuralPath = getStructuralPath(accountPath);
  const fullParameterizedAccount = accountPathToAccount.get(structuralPath);
  const account =
    fullParameterizedAccount &&
    reparameterizeSchemaLedgerAccountInput({
      account: { ...fullParameterizedAccount, path: structuralPath },
      entryParameters,
      fillParamOptions: {
        allowMissing: true,
        allowMissingInChildren: true,
      },
    });
  return account;
};

export const validateLineAccount = (
  entryType: string,
  lineKey: string,
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  entryParameters: Record<string, string>,
  options?: { allowPaymentAccount?: boolean }
): ValidationResult[] => {
  const validationResults: ValidationResult[] = safeGetZodErrors(
    ParameterizedString.refine((s) => s.length > 0, {
      message: `Ledger Line (${lineKey}) in Ledger Entry (${entryType}) must specify a Ledger Account path`,
    }).refine((s) => !s.includes('#'), {
      message: `Ledger Line account path '${accountPath}' cannot contain '#'`,
    }),
    accountPath,
    ['account', 'path']
  );

  const structuralPath = getStructuralPath(accountPath);
  const instanceValueMap = getInstanceValueByAccountPath(accountPath);
  Array.from(instanceValueMap.keys()).forEach((ancestorPath) => {
    const structuralAncestorPath = getStructuralPath(ancestorPath);
    const ancestor = accountPathToAccount.get(structuralAncestorPath);
    if (!ancestor) {
      validationResults.push({
        path: ['account'],
        message: `Entry (${entryType}) has a line that refers to a non-existent account path (path: ${accountPath} | line key: ${lineKey})`,
      });
      return;
    }
    if (
      ancestor.template &&
      !ancestor.linkedAccount &&
      instanceValueMap.get(ancestorPath) === undefined
    ) {
      validationResults.push({
        path: ['account'],
        message: `Invalid account path for line. This line refers to a Template Account, but is missing a variable that identifies the instance of the Template Account. For example: ${ancestorPath}:{{var}}. At: (entry: ${entryType} | line key: ${lineKey} | account path: ${accountPath}).`,
      });
    }
  });

  const account = getFullyParameterizedAccount(
    accountPath,
    accountPathToAccount,
    entryParameters
  );

  if (
    instanceValueMap.get(structuralPath) !== undefined &&
    account &&
    !account.template
  ) {
    validationResults.push({
      message: `Account path may only contain variables for template accounts. (Entry: ${entryType}'s |  Line account path: ${accountPath} | Line key: ${lineKey})`,
      path: ['account', 'path'],
    });
  }
  if (
    instanceValueMap.get(structuralPath) === '' &&
    account &&
    account.template
  ) {
    validationResults.push({
      message: `Instance value for template account cannot be empty. (Entry: ${entryType}'s |  Line account path: ${accountPath} | Line key: ${lineKey})`,
      path: ['account', 'path'],
    });
  }

  try {
    if (
      !options?.allowPaymentAccount &&
      account &&
      getSchemaAccountCategory(account) === AccountCategory.Payment
    ) {
      validationResults.push({
        code: 'payment_account_in_ledger_entry',
        message: `Ledger Entries cannot post to Payment Accounts. (entry: ${entryType} | line key: ${lineKey} | account path: ${accountPath})`,
        path: ['account', 'path'],
      });
    }
  } catch (e) {
    // validateAccountCategory already reports the multi category error
    if (!(e instanceof AccountCategoryMutualExclusionError)) {
      throw e;
    }
  }

  return validationResults;
};

export const validateLineCurrency = (
  currency: { code: string; customCurrencyId?: string } | undefined,
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  entryParameters: Record<string, string>,
  lineKey: string,
  entryType: string
): ValidationResult[] => {
  const validationResults: ValidationResult[] = [];
  const account = getFullyParameterizedAccount(
    accountPath,
    accountPathToAccount,
    entryParameters
  );
  if (
    currency &&
    account &&
    account.currencyMode === 'single' &&
    account.currency &&
    account.currency.code !== currency.code
  ) {
    if (
      !getSchemaObjectParameters(currency.code).length &&
      getSchemaObjectParameters(account.currency.code).length
    ) {
      // This is allowed because the static value in the line can be used as a value for
      // the parameter in the account.
      // TODO (FRA-3379): This should only be allowed if the account is a template account or its ancestor is a template account. This check should probably exist at a higher level, not here
    } else if (
      getSchemaObjectParameters(currency.code).length &&
      !getSchemaObjectParameters(account.currency.code).length
    ) {
      validationResults.push({
        path: ['currency', 'code'],
        message: `Currency cannot be parameterized on a line posting to a single-currency account, as '${currency.code}' is not always guaranteed to be '${account.currency.code}' (Entry: ${entryType} | line key: ${lineKey} | account path: ${accountPath})`,
      });
    } else {
      validationResults.push({
        path: ['currency', 'code'],
        message: `Entry ${entryType}'s line ${accountPath} has currency ${currency.code}, which differs from its account's currency ${account.currency?.code}`,
      });
    }
  }
  if (!currency && account && account.currencyMode === 'multi') {
    validationResults.push({
      message: `Entry ${entryType}'s line ${accountPath} must have a currency set for multi-currency account (Line key: ${lineKey}, account key: ${account.key})`,
      path: ['currency'],
    });
  }

  if (currency && !SchemaCurrencyMatchInput.safeParse(currency).success) {
    if (currency.code === 'CUSTOM' && !currency.customCurrencyId) {
      validationResults.push({
        message: `Line must provide custom currency ID for custom currency (Line key: ${lineKey}, entry: ${entryType})`,
        path: ['currency', 'customCurrencyId'],
      });
    } else if (
      currency.customCurrencyId &&
      currency.code !== 'CUSTOM' &&
      !getSchemaObjectParameters(currency.code).length
    ) {
      validationResults.push({
        message: `customCurrencyId cannot be set when currency code is '${currency.code}' (Line key: ${lineKey}, entry: ${entryType}). Set code to 'CUSTOM' or use a parameterized code.`,
        path: ['currency', 'customCurrencyId'],
      });
    } else if (
      currency.customCurrencyId &&
      getSchemaObjectParameters(currency.code).length > 0 &&
      getSchemaObjectParameters(currency.customCurrencyId).length === 0
    ) {
      validationResults.push({
        message: `When currency code is parameterized, customCurrencyId must also be parameterized (Line key: ${lineKey}, entry: ${entryType})`,
        path: ['currency', 'customCurrencyId'],
      });
    } else if (currency.code !== 'CUSTOM') {
      validationResults.push({
        message: `Currency code '${currency.code}' is invalid (Line key: ${lineKey}, entry: ${entryType})`,
        path: ['currency', 'code'],
      });
    }
  }
  return validationResults;
};

export const validateLineTx = (
  tx: { externalId?: string; id?: string } | undefined,
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  entryParameters: Record<string, string>,
  lineKey: string,
  entryType: string
): ValidationResult[] => {
  const validationResults: ValidationResult[] = [];
  const account = getFullyParameterizedAccount(
    accountPath,
    accountPathToAccount,
    entryParameters
  );
  if (tx && account && !account.linkedAccount) {
    validationResults.push({
      message: `Cannot specify tx for an unlinked account. (Entry: ${entryType}'s |  Line account path: ${accountPath} | Line key: ${lineKey})`,
      path: ['tx'],
    });
  }

  if (!tx && account && account.linkedAccount) {
    validationResults.push({
      message: `Must specify tx for a linked account. (Entry: ${entryType}'s |  Line account path: ${accountPath} | Line key: ${lineKey})`,
      path: ['tx'],
    });
  }

  if (account && account.linkedAccount && tx && !(tx.externalId || tx.id)) {
    validationResults.push({
      message: `Must specify one of external ID or ID for the tx. (Entry: ${entryType}'s |  Line account path: ${accountPath} | Line key: ${lineKey})`,
      path: ['tx'],
    });
  }
  return validationResults;
};

export const validateLine = (
  entryType: string,
  entryParameters: Record<string, string> | undefined,
  entryHasTwoLines: boolean,
  entryHasAnyLineWithTx: boolean,
  line: ReturnType<typeof fillIn>['types'][number]['lines'][number],
  accountPathToAccount: Map<string, ValidatedAccount>,
  options?: { allowPaymentAccount?: boolean }
): ValidationResult[] => {
  const validationResult = validateLineKey(line.key);

  const accountPath = line.account.path;
  const parameters = entryParameters ?? {};

  validationResult.push(
    ...validateLineAmount(
      line.amount,
      entryType,
      line.account.path,
      line.key,
      entryHasTwoLines,
      entryHasAnyLineWithTx
    )
  );

  validationResult.push(
    ...validateLineAccount(
      entryType,
      line.key,
      accountPath,
      accountPathToAccount,
      parameters,
      options
    )
  );

  validationResult.push(
    ...validateLineCurrency(
      line.currency ?? undefined,
      accountPath,
      accountPathToAccount,
      parameters,
      line.key,
      entryType
    )
  );

  validationResult.push(
    ...validateLineTx(
      line.tx ?? undefined,
      accountPath,
      accountPathToAccount,
      parameters,
      line.key,
      entryType
    )
  );

  // Validate line-level tags using the same rules as entry-level tags
  if (line.tags) {
    validationResult.push(
      // eslint-disable-next-line @typescript-eslint/no-use-before-define -- grandfathered
      ...validateTags(line.tags).map((err) => ({
        ...err,
        // Prefix the path with the line's key for better error messages
        path: ['lines', line.key, ...err.path],
      }))
    );
  }

  return validationResult;
};

const isKDefined = <A, K extends string>(
  k: K,
  line: {
    [P in K]?: A;
  }
): line is { [P in K]: A } => {
  return line[k] !== undefined;
};

type FilledInLine = ReturnType<typeof fillIn>['types'][number]['lines'][number];

const validateBalancedLineGroup = (
  entryType: string,
  entryParameters: Record<string, string> | undefined,
  lines: FilledInLine[],
  accountPathToAccount: Map<string, ValidatedAccount>,
  options?: { allowPaymentAccount?: boolean }
): ValidationResult[] => {
  const parameterizedLines = lines
    .map((line) =>
      parameterizeSchemaLedgerLineInput({
        line,
        entryParameters,
      })
    )
    .map((line) => ({
      ...line,
      accountPath: line.account.path,
      account: accountPathToAccount.get(getStructuralPath(line.account.path))
        ? {
            ...accountPathToAccount.get(getStructuralPath(line.account.path)),
            path: line.account.path,
          }
        : undefined,
    }))
    .map((line) => ({
      ...line,
      currency: line.currency ?? line.account?.currency,
    }))
    .filter((line) => isKDefined('currency' as const, line))
    .filter(
      (line) =>
        isKDefined('account' as const, line) &&
        validateLine(
          entryType,
          entryParameters,
          lines.length === 2,
          lines.some((l) => l.tx),
          line,
          accountPathToAccount,
          options
        ).length === 0
    );

  // If there's any errors in the lines, we shouldn't even try to run the balance checks on them.
  // Otherwise, the balance checks will be noisy and might obfuscate the real problem
  if (parameterizedLines.length < lines.length) {
    return [];
  }
  type Line = (typeof parameterizedLines)[number];
  try {
    assertBalancedLinesSchemas({
      lines: parameterizedLines as (Line & {
        account: NonNullable<Line['account']>;
        currency: NonNullable<Line['currency']>;
      })[],
      entryType,
    });
  } catch (e) {
    if (e instanceof Error) {
      return [
        {
          message: e.message,
          path: ['lines'],
        },
      ];
    }
    return [];
  }
  return [];
};

export const validateBalancedLines = (
  entryType: string,
  entryParameters: Record<string, string> | undefined,
  lines: FilledInLine[],
  accountPathToAccount: Map<string, ValidatedAccount>,
  options?: { allowPaymentAccount?: boolean }
): ValidationResult[] => {
  const staticLines = lines.filter((line) => !line.repeated);
  const repeatedGroups = new Map<string, FilledInLine[]>();
  lines.forEach((line) => {
    if (line.repeated) {
      const group = repeatedGroups.get(line.repeated.key) ?? [];
      group.push(line);
      repeatedGroups.set(line.repeated.key, group);
    }
  });

  const errors: ValidationResult[] = [];
  if (staticLines.length > 0) {
    errors.push(
      ...validateBalancedLineGroup(
        entryType,
        entryParameters,
        staticLines,
        accountPathToAccount,
        options
      )
    );
  }
  repeatedGroups.forEach((groupLines, key) => {
    errors.push(
      ...validateBalancedLineGroup(
        entryType,
        entryParameters,
        groupLines,
        accountPathToAccount,
        options
      ).map((error) => ({
        ...error,
        message: `[repeated key '${key}'] ${error.message}`,
      }))
    );
  });
  return errors;
};

export const validateEntryConditions = (
  entryConditions: ReturnType<typeof fillIn>['types'][number]['conditions'],
  staticParameters: Record<string, string> | undefined,
  entryType: string,
  entryStatus: SchemaLedgerEntityStatus,
  accountPathToAccount: Map<string, ValidatedAccount>,
  defaultConsistencyConfig?: { totalBalanceUpdates?: 'strong' | 'eventual' }
): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  const conditions = entryConditions.map((condition) =>
    parameterizeSchemaLedgerEntryConditionInput({
      condition,
      entryParameters: staticParameters,
      fillParamOptions: {
        allowMissing: true,
        allowMissingInChildren: true,
      },
    })
  );

  const isTotalBalanceCondition = (
    condition: ReturnType<typeof fillIn>['types'][number]['conditions'][number]
  ) => {
    return (
      Boolean(condition.precondition?.totalBalance) ||
      Boolean(condition.postcondition?.totalBalance)
    );
  };

  const isOwnBalanceCondition = (
    condition: ReturnType<typeof fillIn>['types'][number]['conditions'][number]
  ) => {
    return (
      Boolean(condition.precondition?.ownBalance) ||
      Boolean(condition.postcondition?.ownBalance)
    );
  };

  const isTotalBalance = isTotalBalanceSchema(defaultConsistencyConfig);

  conditions.forEach((condition, idx) => {
    const hasOwnBalanceCondition = isOwnBalanceCondition(condition);
    const hasTotalBalanceCondition = isTotalBalanceCondition(condition);

    if (isTotalBalance && hasOwnBalanceCondition) {
      errors.push({
        path: ['conditions', idx],
        message: `Schema uses totalBalance but entry type "${entryType}" has ownBalance conditions. Use totalBalance conditions instead.`,
      });
    }
    if (!isTotalBalance && hasTotalBalanceCondition) {
      errors.push({
        path: ['conditions', idx],
        message: `Schema uses ownBalance but entry type "${entryType}" has totalBalance conditions. Use ownBalance conditions instead.`,
      });
    }
    // This needs to be structural path?
    const structuralPath = getStructuralPath(condition.account.path);

    const fullyParameterizedMatchedAccount =
      accountPathToAccount.get(structuralPath);
    // We reparameterize the account here to ensure our validation checks remain valid.
    const matchedAccount = fullyParameterizedMatchedAccount
      ? reparameterizeSchemaLedgerAccountInput({
          account: {
            ...fullyParameterizedMatchedAccount,
            path: condition.account.path,
          },
          entryParameters: staticParameters,
          fillParamOptions: {
            allowMissing: true,
            allowMissingInChildren: true,
          },
        })
      : undefined;
    if (matchedAccount?.linkedAccount) {
      errors.push({
        path: ['conditions', idx, 'account', 'path'],
        message: `You may not specify a condition on a Linked Ledger Account. Error occurred at (path: ${condition.account.path} | entry: ${entryType})`,
      });
    }
    const instanceValueMap = getInstanceValueByAccountPath(
      condition.account.path
    );
    Array.from(instanceValueMap.keys()).forEach((ancestorPath) => {
      const structuralAncestorPath = getStructuralPath(ancestorPath);
      const ancestor = accountPathToAccount.get(structuralAncestorPath);
      if (!ancestor) {
        errors.push({
          path: ['conditions', idx, 'account'],
          message: `Condition refers to a non-existent account path. Error occurred at (path: ${condition.account.path} | entry: ${entryType})`,
        });
        return;
      }
      if (ancestor.template && !instanceValueMap.get(ancestorPath)) {
        errors.push({
          path: ['conditions', idx, 'account'],
          message: `Invalid account path for condition. This condition refers to a Template Account, but is missing a variable that identifies the instance of the Template Account. For example: ${ancestorPath}:{{var}}. Error occurred at (path: ${condition.account.path} | entry: ${entryType})`,
        });
      }
    });
    if (
      (matchedAccount?.currencyMode === 'multi' &&
        condition.currency === undefined) ||
      condition.currency?.code.trim() === ''
    ) {
      errors.push({
        path: ['conditions', idx, 'currency'],
        message: `You must specify a currency for a condition on a multi-currency Ledger Account. Error occurred at (${condition.account.path} | entry: ${entryType})`,
      });
    }
    if (
      matchedAccount?.currencyMode === 'single' &&
      condition.currency !== undefined &&
      condition.currency?.code !== matchedAccount.currency?.code
    ) {
      errors.push({
        path: ['conditions', idx, 'currency', 'code'],
        message: `The currency you specified on a condition (${
          condition.currency?.code === 'CUSTOM'
            ? condition.currency.customCurrencyId
            : condition.currency.code
        }) differs from the currency of the Ledger Account it applies to. Error occurred at (${
          matchedAccount.currency?.code
        } | entry: ${entryType})`,
      });
    }
    // Check if condition uses ownBalance and account has strong ownBalanceUpdates consistency
    if (
      isOwnBalanceCondition(condition) &&
      matchedAccount &&
      matchedAccount.consistencyConfig.ownBalanceUpdates !== 'strong'
    ) {
      errors.push({
        path: ['conditions', idx],
        message: `You cannot specify an ownBalance condition on a Ledger Account whose ownBalanceUpdates consistency mode is not 'strong'. Error occurred at (path: ${condition.account.path} | entry: ${entryType})`,
      });
    }
    // do not allow both pre and post conditions for a total balance
    if (
      isTotalBalanceCondition(condition) &&
      condition.precondition &&
      condition.postcondition
    ) {
      errors.push({
        path: ['conditions', idx, 'precondition', 'postcondition'],
        message: `You may not specify both precondition and postcondition on the same condition. Error occurred at (${condition.account.path} | entry: ${entryType})`,
      });
    }
    (['precondition', 'postcondition'] as const).forEach((condType) => {
      // Prevent both ownBalance and totalBalance in the same condition
      if (
        condition?.[condType]?.ownBalance &&
        condition?.[condType]?.totalBalance
      ) {
        errors.push({
          path: ['conditions', idx, condType, 'ownBalance', 'totalBalance'],
          message: `You may not specify both ownBalance and totalBalance on the same condition. Error occurred at (${condition.account.path} | entry: ${entryType})`,
        });
      }

      // Validate ownBalance properties
      if (condition?.[condType]?.ownBalance) {
        (['gte', 'lte', 'eq'] as const).forEach((prop) => {
          errors.push(
            ...safeGetZodErrors(
              ParameterizedAmount({
                optional: true,
                errorMessage: `Invalid expression '${condition?.[condType]?.ownBalance?.[prop]}' provided to condition ${condType}.ownBalance.${prop} (entry: ${entryType})`,
              }).refine(
                (str) => (typeof str === 'string' ? str.length > 0 : true),
                {
                  message: `Invalid expression '${condition?.[condType]?.ownBalance?.[prop]}' provided to condition ${condType}.ownBalance.${prop} (entry: ${entryType}).`,
                }
              ),
              condition?.[condType]?.ownBalance?.[prop],
              ['conditions', idx, condType, 'ownBalance', prop]
            )
          );
        });
      }

      // Validate totalBalance properties
      if (condition?.[condType]?.totalBalance) {
        if (Object.keys(condition?.[condType]?.totalBalance ?? {}).length > 1) {
          errors.push({
            path: ['conditions', idx, condType, 'totalBalance'],
            message: `You may not specify multiple comparators on the same condition. Error occurred at (${condition.account.path} | entry: ${entryType})`,
          });
        }
        (['gte', 'lte', 'eq'] as const).forEach((prop) => {
          errors.push(
            ...safeGetZodErrors(
              ParameterizedAmount({
                optional: true,
                errorMessage: `Invalid expression '${condition?.[condType]?.totalBalance?.[prop]}' provided to condition ${condType}.totalBalance.${prop} (entry: ${entryType})`,
              }).refine(
                (str) => (typeof str === 'string' ? str.length > 0 : true),
                {
                  message: `Invalid expression '${condition?.[condType]?.totalBalance?.[prop]}' provided to condition ${condType}.totalBalance.${prop} (entry: ${entryType}).`,
                }
              ),
              condition?.[condType]?.totalBalance?.[prop],
              ['conditions', idx, condType, 'totalBalance', prop]
            )
          );
        });
      }

      if (condition?.[condType]?.totalBalance) {
        const totalBalanceConsistencyMode =
          matchedAccount?.consistencyConfig?.totalBalanceUpdates ??
          defaultConsistencyConfig?.totalBalanceUpdates;
        if (
          entryStatus === 'active' &&
          totalBalanceConsistencyMode !== 'strong'
        ) {
          errors.push({
            path: ['conditions', idx, condType, 'totalBalance'],
            message: `You cannot supply a totalBalance condition on an account whose totalBalance consistency mode is not 'strong'.`,
          });
        }
      }
    });
  });
  return errors;
};

const parameterRegex = /{{[^{}]*}}/;

/** Find the minimum string length possible for a parameterized string */
export const getParameterizedStringMinLength = (value: string) => {
  let sampleValue = value;
  while (sampleValue.match(parameterRegex)) {
    sampleValue = sampleValue.replace(parameterRegex, '_');
  }
  // We can validate the returned length to ensure that it's possible to fill parameters and fit within a length requirement.
  return sampleValue.length;
};

export const validateDescription = (description?: string) => {
  const errors: ValidationResult[] = [];
  if (description) {
    errors.push(
      ...safeGetZodErrors(ParameterizedString, description, ['description'])
    );
  }
  return errors;
};

export const validateTags = (
  tags: ReturnType<typeof fillIn>['types'][number]['tags'],
  entityName: 'Ledger Entries' | 'Ledger Lines' = 'Ledger Entries'
) => {
  const errors: ValidationResult[] = [];
  const keyValues = new Map<string, string>();
  tags.forEach(({ key, value }, i) => {
    if (!value || !key) {
      errors.push({
        path: ['tags', i, !key ? 'key' : 'value'],
        message:
          'Tag Keys and Values may not be empty or whitespace-only strings.',
      });
    } else {
      if (keyValues.has(key) && keyValues.get(key) !== value) {
        // Hopefully nobody will use this, but we do allow duplicates with the exact same value, in order to provide a consistent API.
        // We want to do this without deduping the whole array, so that the index matches what the user provided.
        errors.push({
          path: ['tags', i, 'key'],
          message: `Tag keys must be unique. Tag key '${key}' already has another value '${keyValues.get(
            key
          )}'.`,
        });
      } else {
        keyValues.set(key, value);
      }
      errors.push(
        ...validateNonEmptySafeString({
          value: key.trim(),
          path: ['tags', i, 'key'],
          name: 'key',
          entityName: 'Tag',
        })
      );
      if (key.length > MAX_TAG_KEY_LENGTH) {
        errors.push({
          path: ['tags', i, 'key'],
          message: `Tag keys must be at most ${MAX_TAG_KEY_LENGTH} characters. You specified ${key.length} characters.`,
        });
      }
      if (value.trim().length === 0) {
        errors.push({
          path: ['tags', i, 'value'],
          message: 'Tag values may not be empty or whitespace-only strings.',
        });
      }
      errors.push(
        ...safeGetZodErrors(
          ParameterizedString.refine(
            (s) => !s.includes('#') && !s.includes('/') && !s.includes(':'),
            {
              message: `Tag value '${value}' cannot contain '#', '/', or ':'`,
            }
          ),
          value
        ).map(({ path: _, ...rest }) => ({
          // zod requires string args for path, and we want to use a number.
          // we'll just provide our own path.
          path: ['tags', i, 'value'],
          ...rest,
        }))
      );
      const minLength = getParameterizedStringMinLength(value);
      if (minLength > MAX_TAG_VALUE_LENGTH) {
        errors.push({
          path: ['tags', i, 'value'],
          message: `Tag values must be at most ${MAX_TAG_VALUE_LENGTH} characters. Length after filling all parameters is at least ${minLength}.`,
        });
      }
    }
  });
  if (keyValues.size > MAX_TAG_COUNT) {
    errors.push({
      path: ['tags'],
      message: `${entityName} can have up to ${MAX_TAG_COUNT} unique tags. You specified ${keyValues.size} unique tags.`,
    });
  }
  return errors;
};

export const validateGroups = (groups: { key: string; value: string }[]) => {
  const errors: ValidationResult[] = [];
  const keyValues = new Map<string, string>();
  // validate safestring
  groups.forEach(({ key }, i) => {
    const keyResult = SafeStringSchema.safeParse(key);
    if (!keyResult.success) {
      errors.push({
        path: ['groups', i, 'key'],
        message: `Group key '${key}' cannot contain '#', '/', ':', or parameters in {{handlebar}} syntax`,
      });
    }
  });
  groups.forEach(({ key, value }, i) => {
    if (!value || !key) {
      errors.push({
        path: ['groups', i, !key ? 'key' : 'value'],
        message:
          'Group Keys and Values may not be empty or whitespace-only strings.',
      });
    } else {
      if (keyValues.has(key) && keyValues.get(key) !== value) {
        // Hopefully nobody will use this, but we do allow duplicates with the exact same value, in order to provide a consistent API.
        // We want to do this without deduping the whole array, so that the index matches what the user provided.
        errors.push({
          path: ['groups', i, 'key'],
          message: `Group keys must be unique. Group key '${key}' already has another value '${keyValues.get(
            key
          )}'.`,
        });
      } else {
        keyValues.set(key, value);
      }
      errors.push(
        ...validateNonEmptySafeString({
          value: key.trim(),
          path: ['groups', i, 'key'],
          name: 'key',
          entityName: 'Group',
        })
      );
      if (key.length > 128) {
        errors.push({
          path: ['groups', i, 'key'],
          message: `Group keys must be at most 128 characters. You specified ${key.length} characters.`,
        });
      }
      if (value.trim().length === 0) {
        errors.push({
          path: ['groups', i, 'value'],
          message: 'Group values may not be empty or whitespace-only strings.',
        });
      }
      errors.push(
        ...safeGetZodErrors(
          ParameterizedString.refine(
            (s) => !s.includes('#') && !s.includes('/') && !s.includes(':'),
            {
              message: `Group value '${value}' cannot contain '#', '/', or ':'`,
            }
          ),
          value
        ).map(({ path: _, ...rest }) => ({
          // zod requires string args for path, and we want to use a number.
          // we'll just provide our own path.
          path: ['groups', i, 'value'],
          ...rest,
        }))
      );
      const minLength = getParameterizedStringMinLength(value);
      if (minLength > 128) {
        errors.push({
          path: ['groups', i, 'value'],
          message: `Group values must be at most 128 characters. Length after filling all parameters is at least ${minLength}.`,
        });
      }
    }
  });
  if (keyValues.size > 10) {
    errors.push({
      path: ['groups'],
      message: `Ledger Entries can have up to 10 unique groups. You specified ${keyValues.size} unique groups.`,
    });
  }
  return errors;
};

// One synchronous balance write that a line contributes to the entry. We keep
// this as semantic data rather than baking the dedupe key / human label into
// strings at the contribution site — `dedupeKeyFor` and `labelFor` below are
// the only places that decide how a contribution is rendered.
type BalanceContribution =
  // A subpath write — either the leaf account itself or one of its
  // ancestors when the schema's totalBalanceUpdates is 'strong'. At runtime
  // these all map to a single LatestLedgerAccountBalance doc keyed by
  // (subpath, currency); see makeAccountHash in
  // libs/ledger-commands-utils/src/utils/getPayloadForStoringLedgerEntry.ts.
  | { kind: 'subpath'; subpath: string; currencyKey: string }
  // A group balance write — stored in a separate doc per (account, group key,
  // currency), so it never collides with the subpath namespace.
  | {
      kind: 'group';
      groupKey: string;
      templatePath: string;
      currencyKey: string;
    };

const dedupeKeyFor = (c: BalanceContribution): string =>
  match(c)
    .with(
      { kind: 'subpath' },
      ({ subpath, currencyKey }) => `subpath:${subpath}:${currencyKey}`
    )
    .with(
      { kind: 'group' },
      ({ groupKey, templatePath, currencyKey }) =>
        `group:${groupKey}:${templatePath}:${currencyKey}`
    )
    .exhaustive();

const labelFor = (c: BalanceContribution): string =>
  match(c)
    .with({ kind: 'subpath' }, ({ subpath }) => subpath)
    .with(
      { kind: 'group' },
      ({ groupKey }) => `group key "${groupKey}" ownBalance`
    )
    .exhaustive();

export const validateConsistentBalanceUpdates = (
  lines: ReturnType<typeof fillIn>['types'][number]['lines'],
  groups: { key: string | SafeString; value: string }[],
  accountPathToAccount: Map<string, ValidatedAccount>,
  defaultConsistencyConfig: {
    totalBalanceUpdates?: 'strong' | 'eventual';
    ownBalanceUpdates?: 'strong' | 'eventual';
  } = {}
): ValidationResult[] => {
  const groupsInEntry = new Set(groups.map(({ key }) => key));

  // Keep track of two things:
  // 1) Which balances are updated by each line (for error messaging later)
  // 2) Which unique balances are updated by the entry as a whole (for error checking)
  const lineResults = lines.map((line, idx) => {
    const templatePath = line.account.path;
    const structuralPath = getStructuralPath(templatePath);
    const account = accountPathToAccount.get(structuralPath);
    // It's okay to lump together lines with missing currencies, since they
    // will result in errors elsewhere in the validation process.
    const currencyKey = `${line.currency?.code ?? '<unknown>'}:${
      line.currency?.customCurrencyId ?? '_'
    }`;

    // Walk every subpath of the line's account and emit one contribution per
    // subpath whose consistency config triggers
    // `shouldUpdateLatestLedgerAccountBalanceSynchronously` in
    // libs/ledger-commands-utils/src/utils/getPayloadForStoringLedgerEntry.ts.
    //
    // We key each contribution by its TEMPLATE subpath (plus currency), which
    // matches runtime coalescing in `balanceDeltaByAccountKey`:
    //   - Two lines whose template subpaths are identical share entry
    //     parameters, so they resolve to the same concrete account and one
    //     DynamoDB balance doc — count once. This includes the case where one
    //     line's leaf is another line's ancestor.
    //   - Two lines with different template strings (e.g.
    //     `customers/customer:{{a}}/...` vs `customers/customer:{{b}}/...`,
    //     or any literal-vs-template mix) may resolve to distinct concrete
    //     ancestors, so we conservatively count them separately.
    const templateSubpaths = getSubpaths(templatePath);
    const structuralSubpaths = getStructuralSubpaths(templatePath);
    const subpathContributions: BalanceContribution[] =
      templateSubpaths.flatMap((subpath, subpathIdx) => {
        // Every account in the COA tree (leaves and ancestors) is registered
        // in `accountPathToAccount` by getStructuralPathToAccount, so a miss
        // here only happens when the line references an account that doesn't
        // exist in the schema. That case is already reported as a friendly
        // "non-existent account path" error by validateBalancedLines →
        // validateLine, which runs in the same validateLedgerEntry pass. We
        // skip silently here to avoid duplicating that error.
        const subpathAccount = accountPathToAccount.get(
          structuralSubpaths[subpathIdx]
        );
        if (!subpathAccount) return [];

        // For totalBalance schemas, coa.ts intentionally does NOT inline the
        // schema-level default into each account's `totalBalanceUpdates` —
        // it leaves the field undefined unless the account explicitly
        // overrides it (see getBalanceUpdatesConfig in coa.ts). The runtime
        // applies the fallback at lookup time
        // (libs/ledger-commands-utils/src/utils/getAccountConfigMap.ts:
        // buildAccountConfigFromSchema), so we mirror that here. (For
        // ownBalance schemas the default IS inlined, so subpathConfig
        // .ownBalanceUpdates already reflects the effective value.)
        const subpathConfig = subpathAccount.consistencyConfig;
        const totalBalanceUpdates =
          subpathConfig.totalBalanceUpdates ??
          defaultConsistencyConfig.totalBalanceUpdates;
        const isLeaf = subpath === templatePath;
        const triggersSyncWrite =
          totalBalanceUpdates === 'strong' ||
          (isLeaf && subpathConfig.ownBalanceUpdates === 'strong');
        if (!triggersSyncWrite) return [];

        return [{ kind: 'subpath', subpath, currencyKey }];
      });

    const groupContributions: BalanceContribution[] =
      account?.consistencyConfig.groups?.flatMap(({ key, ownBalanceUpdates }) =>
        groupsInEntry.has(key) && ownBalanceUpdates === 'strong'
          ? [
              {
                kind: 'group',
                groupKey: key,
                templatePath,
                currencyKey,
              } as const,
            ]
          : []
      ) ?? [];

    return {
      idx,
      contributions: [...subpathContributions, ...groupContributions],
    };
  });

  const uniqueBalancesUpdated = new Set(
    lineResults.flatMap((r) => r.contributions.map(dedupeKeyFor))
  );

  const lineMessages: ValidationResult[] = lineResults.flatMap((r) => {
    if (r.contributions.length === 0) return [];
    return [
      {
        path: ['lines', r.idx],
        message: `This line updates the following ${
          r.contributions.length
        } balances: [${r.contributions.map(labelFor).join(', ')}]`,
      },
    ];
  });

  if (uniqueBalancesUpdated.size > MAX_CONSISTENT_BALANCE_UPDATES) {
    return [
      {
        path: [],
        message: `Entry has too many consistent balance updates. The maximum allowed is ${MAX_CONSISTENT_BALANCE_UPDATES}, but this entry updates ${uniqueBalancesUpdated.size} unique balances. See other errors for details`,
      },
      ...lineMessages,
    ];
  }
  return [];
};

export const validateParameters = (
  parameters: Record<string, string> | undefined
) => {
  const errors: ValidationResult[] = [];
  if (parameters) {
    // ensure neither key nor value are empty strings for parameters
    Object.entries(parameters).forEach(([key, value], idx) => {
      if (key === '') {
        errors.push({
          path: ['parameters', idx, 'key'],
          message: `Key for static value at index ${idx} cannot be an empty string.`,
        });
      }
      if (value === '') {
        errors.push({
          path: ['parameters', idx, 'value'],
          message: `Value for static value at index ${idx} cannot be an empty string.`,
        });
      }
    });
  }
  return errors;
};

const normalizeRegex = /{{\s*([^{}\s]*)\s*}}/g;
export const normalizeParameterizedString = (value: string) =>
  value.replace(normalizeRegex, '{{$1}}');

export const validateEntryAndAccountStatus = ({
  lines,
  lineAccountStatuses,
  conditions,
  conditionAccountStatuses,
  entryStatus,
  entryType,
  entryVersion,
}: {
  lines: ReturnType<typeof fillIn>['types'][number]['lines'];
  lineAccountStatuses: Map<string, SchemaLedgerAccountStatus | undefined>;
  conditions: ReturnType<typeof fillIn>['types'][number]['conditions'];
  conditionAccountStatuses: Map<string, SchemaLedgerAccountStatus | undefined>;
  entryStatus: SchemaLedgerEntityStatus;
  entryType: string;
  entryVersion: number;
}): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  const isEntryActive = entryStatus === 'active';

  // Validate that the entry's status is what we expect it to be.
  lines.forEach((line) => {
    const accountStatus = getAccountStatus({
      status: lineAccountStatuses.get(line.account.path),
    });

    if (accountStatus !== 'active' && isEntryActive) {
      errors.push({
        code: 'invalid_account_status',
        path: ['status'],
        message: `Entry (${entryType} V${entryVersion}) has a line that refers to an account with status '${accountStatus}'. Only active accounts can be used in entries.`,
      });
    }
  });
  conditions.forEach((condition) => {
    const accountStatus = getAccountStatus({
      status: conditionAccountStatuses.get(condition.account.path),
    });
    if (accountStatus !== 'active' && isEntryActive) {
      errors.push({
        code: 'invalid_account_status',
        path: ['status'],
        message: `Entry (${entryType} V${entryVersion}) has a condition that refers to an account with status '${accountStatus}'. Only active accounts can be used in entries.`,
      });
    }
  });
  return errors;
};

export const validateEntry = (
  ledgerEntry: Omit<ReturnType<typeof fillIn>['types'][number], 'groups'> & {
    groups: { key: string; value: string }[];
  },
  accountPathToAccount: Map<string, ValidatedAccount>, // TODO - in the designer we don't have unique paths
  // for accounts. But we want to be able to validate and transform before we apply IDs from enhance.
  // I'm not sure how to handle this.
  defaultConsistencyConfig?: { totalBalanceUpdates?: 'strong' | 'eventual' }
): ValidationResult[] => {
  const errors = validateNonEmptySafeString({
    value: ledgerEntry.type,
    path: ['type'],
    name: 'type',
    entityName: 'Ledger Entry',
  });
  if (ledgerEntry.lines.length > MAX_ENTRY_LINES) {
    errors.push({
      path: ['lines'],
      message: `Entry (${ledgerEntry.type}) has too many lines. The maximum allowed is ${MAX_ENTRY_LINES}, but this entry has ${ledgerEntry.lines.length}.`,
    });
  }

  errors.push(
    ...validateEntryAndAccountStatus({
      lines: ledgerEntry.lines,
      lineAccountStatuses: new Map(
        ledgerEntry.lines.map((line) => [
          line.account.path,
          accountPathToAccount.get(getStructuralPath(line.account.path))
            ?.status,
        ])
      ),
      conditions: ledgerEntry.conditions,
      conditionAccountStatuses: new Map(
        ledgerEntry.conditions.map((condition) => [
          condition.account.path,
          accountPathToAccount.get(getStructuralPath(condition.account.path))
            ?.status,
        ])
      ),
      entryStatus: getEntryStatus(ledgerEntry),
      entryType: ledgerEntry.type,
      entryVersion: getEntryTypeVersion(ledgerEntry),
    })
  );
  errors.push(
    ...ledgerEntry.lines.flatMap((line, idx) => {
      // Scope heuristics to the line's group (static or its repeated key)
      const groupLines = line.repeated
        ? ledgerEntry.lines.filter(
            (l) => l.repeated?.key === line.repeated?.key
          )
        : ledgerEntry.lines.filter((l) => !l.repeated);
      return validateLine(
        ledgerEntry.type,
        ledgerEntry.parameters,
        groupLines.length === 2,
        groupLines.some((l) => l.tx),
        parameterizeSchemaLedgerLineInput({
          line,
          entryParameters: ledgerEntry.parameters,
        }),
        accountPathToAccount
      ).map(({ path, ...rest }) => ({
        path: ['lines', idx, ...path],
        ...rest,
      }));
    })
  );
  errors.push(...validateDescription(ledgerEntry.description));
  errors.push(...validateTags(ledgerEntry.tags));
  errors.push(...validateGroups(ledgerEntry.groups));
  // Validate unique line keys globally across all groups (static and repeated)
  errors.push(
    ...validateUniqueByKey({
      key: 'key',
      items: ledgerEntry.lines,
      pathPrefix: ['lines'],
      getErrorMessage: (item) =>
        `Entry (${ledgerEntry.type}) already has a line with key '${item.key}'`,
    })
  );
  // Validate repeated key groups: one key total across BOTH lines and conditions
  const lineRepeatedKeys = new Set(
    ledgerEntry.lines
      .filter((line) => line.repeated)
      .map((line) => line.repeated!.key)
  );
  const conditionRepeatedKeys = new Set(
    ledgerEntry.conditions
      .filter((condition) => condition.repeated)
      .map((condition) => condition.repeated!.key)
  );
  const allRepeatedKeys = new Set<string>([
    ...lineRepeatedKeys,
    ...conditionRepeatedKeys,
  ]);
  if (allRepeatedKeys.size > 1) {
    errors.push({
      path: ['lines'],
      message: `Entry (${ledgerEntry.type}) has ${
        allRepeatedKeys.size
      } repeated key groups across lines and conditions (${[...allRepeatedKeys]
        .map((k) => `'${k}'`)
        .join(
          ', '
        )}). Only one repeated key group is allowed per entry. If you need multiple repeated key groups, please reach out.`,
    });
  }
  // Each repeated key group must have at least 2 lines (conditions don't need to balance)
  lineRepeatedKeys.forEach((key) => {
    const groupSize = ledgerEntry.lines.filter(
      (line) => line.repeated?.key === key
    ).length;
    if (groupSize < 2) {
      errors.push({
        path: ['lines'],
        message: `Entry (${ledgerEntry.type}) repeated key '${key}' must have at least 2 lines.`,
      });
    }
  });
  // A template parameter in the entry description, tags, groups, or non-repeated
  // lines/conditions cannot share a name with a repeated key.
  if (allRepeatedKeys.size > 0) {
    const entryOnlyParams = getSchemaObjectParameters({
      ...ledgerEntry,
      lines: undefined,
      conditions: undefined,
    });
    const staticParams = [
      ...ledgerEntry.lines.filter((line) => !line.repeated),
      ...ledgerEntry.conditions.filter((cond) => !cond.repeated),
    ].flatMap((item) => getSchemaObjectParameters(item));
    const collidingKeys = [...entryOnlyParams, ...staticParams].filter((p) =>
      allRepeatedKeys.has(p)
    );
    collidingKeys.forEach((key) => {
      errors.push({
        path: ['lines'],
        message: `Entry (${ledgerEntry.type}) has a template parameter '${key}' that collides with repeated key '${key}'. Rename the parameter or the repeated key to avoid ambiguity.`,
      });
    });
  }
  errors.push(
    ...validateBalancedLines(
      ledgerEntry.type,
      ledgerEntry.parameters,
      ledgerEntry.lines,
      accountPathToAccount
    )
  );
  errors.push(
    ...validateEntryConditions(
      ledgerEntry.conditions,
      ledgerEntry.parameters,
      ledgerEntry.type,
      getEntryStatus(ledgerEntry),
      accountPathToAccount,
      defaultConsistencyConfig
    )
  );
  errors.push(
    ...validateConsistentBalanceUpdates(
      ledgerEntry.lines,
      ledgerEntry.groups,
      accountPathToAccount,
      defaultConsistencyConfig
    )
  );
  errors.push(...validateParameters(ledgerEntry.parameters));
  const lineTxs = ledgerEntry.lines.flatMap((line) =>
    line.tx ? [line.tx] : []
  );
  if (lineTxs.length > 2) {
    errors.push({
      path: ['lines'],
      message: `Entry (${ledgerEntry.type}) has more than two lines with tx specified. At most two lines can have tx specified.`,
    });
  }
  const lineTxIds = lineTxs.flatMap((tx) =>
    tx.id ? [normalizeParameterizedString(tx.id)] : []
  );
  if (new Set(lineTxIds).size < lineTxIds.length) {
    errors.push({
      path: ['lines'],
      message: `Entry (${ledgerEntry.type}) has duplicate tx IDs.`,
    });
  }
  const lineTxExternalIds = lineTxs.flatMap((tx) =>
    tx.externalId ? [normalizeParameterizedString(tx.externalId)] : []
  );
  if (new Set(lineTxExternalIds).size < lineTxExternalIds.length) {
    errors.push({
      path: ['lines'],
      message: `Entry (${ledgerEntry.type}) has duplicate tx external IDs.`,
    });
  }
  return errors;
};

export const transformAndValidateEntries = ({
  ledgerEntries,
  validatedCoA,
}: ValidateEntriesParams) => {
  const accountPathToAccount = getStructuralPathToAccount(validatedCoA);
  const filledIn = fillIn({ ledgerEntries, accountPathToAccount });
  const validationResultsForDuplicateTypes: ValidationResult[] = Array.from(
    groupByMap(
      filledIn.types.map((entry, idx) => [entry, idx] as const),
      ([entry]) => entry.type
    )
  )
    .filter(([_type, entries]) => {
      const versions = entries.map(([entry]) => entry.typeVersion ?? 1);
      return versions.length !== new Set(versions).size;
    })
    .flatMap(([_type, entries]) =>
      entries.map(([entry, idx]) => ({
        message: `Entry list cannot have duplicate types '${entry.type}'`,
        path: ['types', idx, 'type'],
      }))
    );
  const errors = [
    ...validationResultsForDuplicateTypes,
    ...filledIn.types.flatMap((entry, idx) =>
      validateEntry(
        entry,
        accountPathToAccount,
        validatedCoA.defaultConsistencyConfig
      ).map(({ path, ...rest }) => ({
        path: ['types', idx, ...path],
        ...rest,
      }))
    ),
  ];
  return {
    filledIn,
    errors,
  };
};
